/**
 * useUserPreference (RW-2507) — one per-user view preference, stored in
 * Postgres and mirrored to localStorage.
 *
 *   const [groupBy, setGroupBy, prefStatus] =
 *     useUserPreference(`${storageKey}_groupBy`, 'template', { enabled: claimReady });
 *
 * The setter accepts a value or an updater function, so it drops into a
 * `useState` call site unchanged.
 *
 * ── The three-step life of a value ────────────────────────────────────
 * 1. SYNCHRONOUS first paint. Initial state comes from the in-memory cache, or
 *    the localStorage mirror, or the default — never from a round trip. This is
 *    the property the ticket asked to preserve, and it is why the panels look
 *    exactly as fast as they did before.
 * 2. RECONCILE. The server value arrives and wins, because it is the copy that
 *    followed the user from their other device.
 * 3. PROMOTE. If the server has never heard of this key but the mirror has a
 *    value, that value is the user's real preference from before this feature
 *    existed — so it is written UP to the server rather than discarded. Without
 *    this step, shipping "your settings stop forgetting themselves" would begin
 *    by forgetting everyone's settings once.
 *
 * ── Options ──────────────────────────────────────────────────────────
 *   enabled     gate the round trip (the panels wait for claimReady)
 *   onError     called when a SAVE fails, for a toast
 *   legacyKey   the old localStorage key, when it differs from `key`
 *   fromLegacy  translate a pre-migration localStorage value into the shape
 *               the store accepts — see the coercions at the foot of this file
 *
 * ── Failure is visible ────────────────────────────────────────────────
 * The third element is `{ error, saving }`. A failed SAVE sets `error` and
 * calls the optional `onError` — panels wire that to their toast, because "your
 * arrangement did not save" is precisely the thing that used to be silent. A
 * failed READ does not surface: the mirrored value is already on screen and
 * still correct, so there is nothing for the user to act on.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  readPreferences,
  writePreference,
  readMirror,
  writeMirror,
  peekCache,
} from '../services/userPreferences';
import { auth } from '../firebase';

// The real token uid, matching what the callable will key the row by — NOT the
// effective/impersonated user a component renders. See services/userPreferences.
function realUid() {
  try {
    return (auth && auth.currentUser && auth.currentUser.uid) || null;
  } catch (e) {
    return null;
  }
}

export default function useUserPreference(key, defaultValue, options = {}) {
  const {
    enabled = true,
    onError = null,
    legacyKey = null,
    fromLegacy = null,
  } = options;

  // Held in refs so neither identity re-runs the sync effect: callers pass
  // object literals (`[]`) and inline arrows, which change every render.
  const defaultRef = useRef(defaultValue);
  defaultRef.current = defaultValue;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const legacyKeyRef = useRef(legacyKey);
  legacyKeyRef.current = legacyKey;
  const fromLegacyRef = useRef(fromLegacy);
  fromLegacyRef.current = fromLegacy;

  /**
   * The mirrored value, with a pre-migration value translated into the shape
   * the store accepts.
   *
   * This is the step that decides whether a migration is invisible. Several of
   * these settings were written as the STRING '1' or '0' — the localStorage
   * idiom for a flag — and their preference rule is a real boolean, so a raw
   * promotion would be rejected by the server on the user's first load. Worse,
   * it would be rejected for exactly the users who had changed the setting, and
   * silently fine for everyone still on the default.
   *
   * A coercion that returns undefined means "this stored value is not
   * recognizable", and the caller falls back to the default rather than
   * promoting junk.
   */
  const readMirrored = (uid) => {
    const mirrored = readMirror(uid, key, { legacyKey: legacyKeyRef.current });
    if (!mirrored.found || !mirrored.legacy || !fromLegacyRef.current) return mirrored;
    const coerced = fromLegacyRef.current(mirrored.value);
    if (coerced === undefined) return { found: false, value: undefined, legacy: true };
    return { found: true, value: coerced, legacy: true };
  };

  const initial = () => {
    const cached = peekCache(key);
    if (cached !== undefined && cached !== null) return cached;
    const mirrored = readMirrored(realUid());
    return mirrored.found ? mirrored.value : defaultRef.current;
  };

  const [value, setValue] = useState(initial);
  const [status, setStatus] = useState({ error: null, saving: false });

  // Mirrors the live value so the setter can resolve an updater function itself
  // and know what to persist — React's setState does not hand back the result.
  const valueRef = useRef(value);

  // `key` changes under a live component (TasksPanel's group order is keyed on
  // the current groupBy), so re-seed during THIS render rather than letting one
  // frame paint the previous key's value. `current` — not `value` — is what the
  // ref and the return both use: `value` is still the outgoing state until
  // React re-renders, and writing that to the ref would leave the setter
  // resolving updaters against the wrong key's data for one commit.
  let current = value;
  const seededKeyRef = useRef(key);
  // Whether the user has changed THIS key since it was seeded. The reconcile
  // below must not overwrite a change the user just made: the round trip is
  // ~200ms, and a groupBy click inside that window would otherwise be reverted
  // by an older server value arriving late — the change flips back on its own,
  // which reads as the screen fighting you. Cleared with the key, because a
  // different preference has its own history.
  const dirtyRef = useRef(false);
  if (seededKeyRef.current !== key) {
    seededKeyRef.current = key;
    dirtyRef.current = false;
    current = initial();
    setValue(current);
  }
  valueRef.current = current;

  const reportSaveFailure = useCallback((error) => {
    setStatus({ error: error || new Error('save failed'), saving: false });
    if (onErrorRef.current) onErrorRef.current(error);
  }, []);

  // ── reconcile + promote ─────────────────────────────────────────────
  useEffect(() => {
    if (!enabled || !key) return undefined;
    const uid = realUid();
    if (!uid) return undefined;

    let cancelled = false;
    readPreferences([key])
      .then((prefs) => {
        // A change made while this was in flight is newer than anything the
        // server can be answering with, so it wins and is already on its way up.
        if (cancelled || dirtyRef.current) return;
        if (Object.prototype.hasOwnProperty.call(prefs, key)) {
          const serverValue = prefs[key];
          valueRef.current = serverValue;
          setValue(serverValue);
          writeMirror(uid, key, serverValue);
          return;
        }
        // Server has nothing. Promote a pre-existing local value if there is
        // one; a value equal to the default is deliberately NOT promoted, so
        // the table only ever holds preferences somebody actually expressed.
        const mirrored = readMirrored(uid);
        if (mirrored.found && !sameValue(mirrored.value, defaultRef.current)) {
          writePreference(key, mirrored.value).catch((e) => {
            if (!cancelled) reportSaveFailure(e);
          });
        }
      })
      .catch(() => {
        // Read failed — the mirrored value is already rendered and remains the
        // best answer available. Deliberately not surfaced; see the header.
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, reportSaveFailure]);

  const update = useCallback((next) => {
    const resolved = typeof next === 'function' ? next(valueRef.current) : next;
    // A no-op costs a round trip and nothing else, so skip it. This is not just
    // thrift: the panels' reorder updaters return the PREVIOUS array unchanged
    // when a drag lands where it started, and TasksPanel's group-order updater
    // does so on every drag that bails, which would otherwise be a write per
    // aborted drag.
    if (sameValue(resolved, valueRef.current)) return;
    dirtyRef.current = true;
    valueRef.current = resolved;
    setValue(resolved);

    const uid = realUid();
    writeMirror(uid, key, resolved);
    if (!enabled || !uid) return;

    setStatus({ error: null, saving: true });
    writePreference(key, resolved)
      .then(() => setStatus({ error: null, saving: false }))
      .catch(reportSaveFailure);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, reportSaveFailure]);

  return [current, update, status];
}

/* ── legacy coercions ───────────────────────────────────────────────────
 * Pass one as `fromLegacy` for a key whose localStorage spelling is not the
 * shape the store holds. Each returns undefined for a value it does not
 * recognize, which the hook reads as "no stored preference" — so a corrupt or
 * hand-edited entry falls back to the default instead of being promoted.
 *
 * These exist because localStorage has one type. '1' and '0' are how every
 * boolean in this codebase was written, and '100' is how every number was;
 * both are strings by the time they come back.
 */

/** '1'/'0' (and the numbers, and real booleans) → true/false. */
export function legacyBoolean(raw) {
  if (raw === true || raw === false) return raw;
  if (raw === 1 || raw === '1') return true;
  if (raw === 0 || raw === '0') return false;
  return undefined;
}

/** A number, or a numeric string, restricted to `allowed`. */
export function legacyNumber(allowed) {
  const set = new Set(allowed);
  return (raw) => {
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isInteger(n) || !set.has(n)) return undefined;
    return n;
  };
}

/** A bare string, restricted to `allowed`. */
export function legacyOneOf(allowed) {
  const set = new Set(allowed);
  return (raw) => (typeof raw === 'string' && set.has(raw) ? raw : undefined);
}

/**
 * Structural equality for the value shapes this store holds — a string, a
 * boolean, a number, or a flat array of strings. Used only to decide whether a
 * mirrored value is worth promoting, so JSON comparison is exact enough and
 * cheap.
 */
function sameValue(a, b) {
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch (e) {
    return false;
  }
}

export { sameValue };
